import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CalendarIcon, Search, Loader2, AlertCircle, TrendingUp, Clock, Activity, AlertTriangle, Download } from 'lucide-react';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn, exportToCSV } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SummaryCard(props) {
  const { title, value, subtitle, icon: Icon, variant } = props;
  
  const getBorderClass = () => {
    const styles = { success: 'border-l-green-500', error: 'border-l-red-500', warning: 'border-l-orange-500', info: 'border-l-blue-500' };
    return styles[variant] || '';
  };
  
  const getIconClass = () => {
    const colors = { success: 'text-green-500', error: 'text-red-500', warning: 'text-orange-500', info: 'text-blue-500' };
    return colors[variant] || '';
  };
  
  return (
    <Card className={`border-l-4 ${getBorderClass()}`}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          </div>
          <Icon className={`h-8 w-8 ${getIconClass()}`} />
        </div>
      </CardContent>
    </Card>
  );
}

function SeverityChart(props) {
  const { data } = props;
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-muted-foreground">Nenhum dado</div>;
  }
  const total = data.reduce((sum, item) => sum + item.value, 0);
  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const pct = total > 0 ? Math.round((item.value / total) * 100) : 0;
        return (
          <div key={index} className="flex items-center gap-3">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
            <span className="text-sm flex-1">{item.name}</span>
            <span className="text-sm font-medium">{item.value}</span>
            <span className="text-xs text-muted-foreground w-12 text-right">{pct}%</span>
          </div>
        );
      })}
    </div>
  );
}

function CategoryChart(props) {
  const { data } = props;
  if (!data || data.length === 0) {
    return <div className="h-48 flex items-center justify-center text-muted-foreground">Nenhum dado</div>;
  }
  const maxValue = Math.max(...data.map(d => d.value));
  return (
    <div className="space-y-3">
      {data.map((item, index) => {
        const w = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
        return (
          <div key={index} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span>{item.name}</span>
              <span className="font-medium">{item.value}</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${w}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function HostsTable(props) {
  const { hosts } = props;
  
  const getColor = (value) => {
    if (value >= 99) return 'text-green-500';
    if (value >= 95) return 'text-orange-500';
    return 'text-red-500';
  };
  
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Host</TableHead>
          <TableHead>Categoria</TableHead>
          <TableHead className="text-right">Disponibilidade</TableHead>
          <TableHead className="text-right">Interrupções</TableHead>
          <TableHead className="text-right">MTTR</TableHead>
          <TableHead className="text-right">MTBF</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {hosts.map((host, index) => (
          <TableRow key={index}>
            <TableCell className="font-mono text-sm">{host.name}</TableCell>
            <TableCell><Badge variant="secondary">{host.category}</Badge></TableCell>
            <TableCell className={cn("text-right font-semibold", getColor(host.availability))}>{host.availability}%</TableCell>
            <TableCell className="text-right">{host.interruptions}</TableCell>
            <TableCell className="text-right font-mono text-sm">{host.mttr}</TableCell>
            <TableCell className="text-right font-mono text-sm">{host.mtbf}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TriggersTable(props) {
  const { triggers } = props;
  if (!triggers || triggers.length === 0) {
    return <p className="text-center text-muted-foreground py-8">Nenhuma trigger acionada</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Host</TableHead>
          <TableHead>Trigger</TableHead>
          <TableHead className="text-right">Ocorrências</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {triggers.map((trigger, index) => (
          <TableRow key={index}>
            <TableCell className="font-mono text-sm">{trigger.host}</TableCell>
            <TableCell className="max-w-md truncate">{trigger.trigger}</TableCell>
            <TableCell className="text-right"><Badge variant="secondary">{trigger.count}</Badge></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function PeriodDashboardPage() {
  const [hostgroups, setHostgroups] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const { getAuthHeader } = useAuthStore();

  useEffect(() => {
    const fetchHostgroups = async () => {
      try {
        const response = await axios.get(`${API}/zabbix/hostgroups`, { headers: getAuthHeader() });
        setHostgroups(response.data);
      } catch (err) {
        setError('Erro ao carregar grupos. Configure a API Zabbix.');
      } finally {
        setLoadingGroups(false);
      }
    };
    fetchHostgroups();
    axios.get(`${API}/orgs`, { headers: getAuthHeader() })
      .then(res => setOrgs(res.data)).catch(() => {});
  }, [getAuthHeader]);

  const selectOrg = async (orgId) => {
    setSelectedOrg(orgId);
    try {
      const res = await axios.get(`${API}/orgs/${orgId}`, { headers: getAuthHeader() });
      setSelectedGroups((res.data.groups || []).map(g => g.zabbix_group_id));
    } catch (_) {
      setError('Erro ao carregar grupos do cliente');
    }
  };

  const handleSearch = async () => {
    if (selectedGroups.length === 0) {
      setError('Selecione pelo menos um grupo');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const payload = {
        group_ids: selectedGroups,
        org_id: selectedOrg || null,
        start: format(startDate, "yyyy-MM-dd'T'HH:mm"),
        end: format(endDate, "yyyy-MM-dd'T'HH:mm")
      };
      const response = await axios.post(`${API}/dashboard/period`, payload, { headers: getAuthHeader() });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  };

  const handlePresetRange = (days) => {
    setStartDate(subDays(new Date(), days));
    setEndDate(new Date());
  };

  const handleExportCSV = () => {
    if (!data) return;
    
    // Export hosts data
    const hostsColumns = [
      { key: 'name', label: 'Host' },
      { key: 'category', label: 'Categoria' },
      { key: 'availability', label: 'Disponibilidade (%)' },
      { key: 'interruptions', label: 'Interrupções' },
      { key: 'mttr', label: 'MTTR' },
      { key: 'mtbf', label: 'MTBF' }
    ];
    
    const filename = `dashboard_periodo_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`;
    exportToCSV(data.hosts, hostsColumns, filename);
  };

  const handleExportTriggersCSV = () => {
    if (!data?.top_triggers) return;
    
    const triggersColumns = [
      { key: 'host', label: 'Host' },
      { key: 'trigger', label: 'Trigger' },
      { key: 'count', label: 'Ocorrências' }
    ];
    
    const filename = `top_triggers_periodo_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}`;
    exportToCSV(data.top_triggers, triggersColumns, filename);
  };

  const toggleGroup = (groupId) => {
    if (selectedGroups.includes(groupId)) {
      setSelectedGroups(selectedGroups.filter(id => id !== groupId));
    } else {
      setSelectedGroups([...selectedGroups, groupId]);
    }
  };

  return (
    <div className="space-y-6" data-testid="period-dashboard">
      <Card>
        <CardHeader><CardTitle className="text-base font-semibold">Filtros</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Cliente/Org</Label>
              <Select value={selectedOrg} onValueChange={selectOrg}>
                <SelectTrigger data-testid="period-org-select">
                  <SelectValue placeholder="Todos os clientes" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.display_name || o.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Grupos de Hosts</Label>
              {loadingGroups ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="select-groups-btn">
                      {selectedGroups.length > 0 ? `${selectedGroups.length} grupo(s)` : 'Selecionar grupos...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <ScrollArea className="h-64 p-4">
                      <div className="space-y-2">
                        {hostgroups.map((group) => (
                          <div key={group.groupid} className="flex items-center space-x-2">
                            <Checkbox 
                              id={group.groupid} 
                              checked={selectedGroups.includes(group.groupid)} 
                              onCheckedChange={() => toggleGroup(group.groupid)} 
                            />
                            <label htmlFor={group.groupid} className="text-sm cursor-pointer truncate">{group.name}</label>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Data Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="start-date-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(startDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDate} onSelect={(date) => date && setStartDate(startOfDay(date))} locale={ptBR} />
                </PopoverContent>
              </Popover>
            </div>
            
            <div className="space-y-2">
              <Label>Data Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="end-date-btn">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(endDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDate} onSelect={(date) => date && setEndDate(endOfDay(date))} locale={ptBR} />
                </PopoverContent>
              </Popover>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={() => handlePresetRange(1)}>24h</Button>
            <Button variant="outline" size="sm" onClick={() => handlePresetRange(7)}>7 dias</Button>
            <Button variant="outline" size="sm" onClick={() => handlePresetRange(15)}>15 dias</Button>
            <Button variant="outline" size="sm" onClick={() => handlePresetRange(30)}>30 dias</Button>
            <Button variant="outline" size="sm" onClick={() => handlePresetRange(90)}>3 meses</Button>
          </div>
          
          <Button onClick={handleSearch} disabled={loading || selectedGroups.length === 0} data-testid="search-btn">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
            Buscar
          </Button>
        </CardContent>
      </Card>

      {data && (
        <>
          {/* Resumo Executivo */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            <SummaryCard title="Interrupções" value={data.summary.total_interruptions} icon={AlertTriangle} variant="warning" />
            <SummaryCard title="Disponibilidade" value={`${data.summary.availability}%`} icon={Activity} variant="success" />
            <SummaryCard title="Inatividade" value={data.summary.total_downtime_str} subtitle={`${data.summary.downtime_percent}%`} icon={Clock} variant="error" />
            <SummaryCard title="Uptime" value={data.summary.total_uptime_str} subtitle={`${data.summary.uptime_percent}%`} icon={Activity} variant="success" />
            <SummaryCard title="MTTR" value={data.summary.mttr} icon={TrendingUp} variant="info" />
            <SummaryCard title="MTBF" value={data.summary.mtbf} icon={Clock} variant="info" />
          </div>

          {/* Métricas de período e grupos */}
          <Card>
            <CardContent className="p-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Período:</span>
                  <p className="font-medium">{data.period}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Grupos:</span>
                  <p className="font-medium">{data.groups.join(', ')}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total de Hosts:</span>
                  <p className="font-medium">{data.summary.total_hosts}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total de Eventos:</span>
                  <p className="font-medium">{data.summary.total_interruptions}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle className="text-base">Eventos por Severidade</CardTitle></CardHeader>
              <CardContent><SeverityChart data={data.severity_distribution} /></CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle className="text-base">Eventos por Tipo de Tecnologia</CardTitle></CardHeader>
              <CardContent><CategoryChart data={data.category_distribution} /></CardContent>
            </Card>
          </div>

          {/* Top Hosts Mais Alarmados */}
          <Card>
            <CardHeader><CardTitle className="text-base">Top Hosts Mais Alarmados (por interrupções)</CardTitle></CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px]">
                <div className="space-y-2">
                  {data.hosts
                    .filter(h => h.interruptions > 0)
                    .sort((a, b) => b.interruptions - a.interruptions)
                    .slice(0, 10)
                    .map((host, index) => (
                      <div key={host.name} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-3">
                          <span className="text-sm font-mono text-muted-foreground w-6">#{index + 1}</span>
                          <div>
                            <span className="text-sm font-medium">{host.name}</span>
                            <p className="text-xs text-muted-foreground">{host.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="secondary">{host.interruptions} interrupções</Badge>
                          <p className={cn("text-xs mt-1", host.availability >= 99 ? 'text-green-500' : host.availability >= 95 ? 'text-orange-500' : 'text-red-500')}>
                            {host.availability}% disponível
                          </p>
                        </div>
                      </div>
                    ))}
                  {data.hosts.filter(h => h.interruptions > 0).length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Nenhum host com interrupções no período</p>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Disponibilidade por Host</CardTitle>
              <Button variant="outline" size="sm" onClick={handleExportCSV}>
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                <HostsTable hosts={data.hosts} />
              </ScrollArea>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Top 10 Triggers Mais Acionadas</CardTitle>
              <Button variant="outline" size="sm" onClick={handleExportTriggersCSV}>
                <Download className="h-4 w-4 mr-2" />
                Exportar CSV
              </Button>
            </CardHeader>
            <CardContent>
              <TriggersTable triggers={data.top_triggers} />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
